import React, { useMemo, useState } from 'react';
import { PlusIcon, UsersIcon, ListTodoIcon, SearchIcon, ZapIcon, PackageIcon, CheckCircleIcon, ClockIcon, PlayCircleIcon, CalendarIcon } from 'lucide-react';
import { CustomerCard, Customer } from '../components/CustomerCard';
import { TaskCard, Task } from '../components/TaskCard';
import { Button } from '../components/Button';
interface CustomerManagementProps {
  customers: Customer[];
  tasks: Task[];
  onOpenCustomerModal: () => void;
  onOpenTaskModal: () => void;
  onTaskStatusChange: (taskId: string, newStatus: Task['status']) => void;
  onEditCustomer: (customer: Customer) => void;
  onEditTask: (task: Task) => void;
  onDeleteCustomer: (customer: Customer) => void;
  onDeleteTask: (task: Task) => void;
  onViewCustomerDetail: (customerId: string) => void;
  viewMode?: 'customers' | 'tasks';
}
export function CustomerManagement({
  customers,
  tasks,
  onOpenCustomerModal,
  onOpenTaskModal,
  onTaskStatusChange,
  onEditCustomer,
  onEditTask,
  onDeleteCustomer,
  onDeleteTask,
  onViewCustomerDetail,
  viewMode = 'customers'
}: CustomerManagementProps) {
  const [customerSearch, setCustomerSearch] = useState('');
  const [taskSearch, setTaskSearch] = useState('');
  const [taskFilter, setTaskFilter] = useState<'Ladeboks' | 'Andet'>('Ladeboks');
  const [statusFilter, setStatusFilter] = useState<Task['status'] | 'all'>('all');
  // Check if callbacks are no-ops (Read-Only mode)
  const isReadOnly = onOpenCustomerModal.toString().includes('!isReadOnly');
  // Filter customers based on search
  const filteredCustomers = useMemo(() => {
    if (!customerSearch.trim()) return customers;
    const query = customerSearch.toLowerCase();
    return customers.filter(customer => customer.name.toLowerCase().includes(query) || customer.customerId.toLowerCase().includes(query) || customer.phone.toLowerCase().includes(query) || customer.email?.toLowerCase().includes(query) || customer.address.toLowerCase().includes(query));
  }, [customers, customerSearch]);
  // Filter tasks based on search, category AND status
  const filteredTasks = useMemo(() => {
    // First filter by category
    let filtered = tasks.filter(task => task.category === taskFilter);
    // Then filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(task => task.status === statusFilter);
    }
    // Finally filter by search
    if (!taskSearch.trim()) return filtered;
    const query = taskSearch.toLowerCase();
    return filtered.filter(task => task.title.toLowerCase().includes(query) || task.customerName?.toLowerCase().includes(query) || task.description.toLowerCase().includes(query));
  }, [tasks, taskSearch, taskFilter, statusFilter]);
  // Count tasks by status for current category
  const statusCounts = useMemo(() => {
    const categoryTasks = tasks.filter(task => task.category === taskFilter);
    return {
      all: categoryTasks.length,
      done: categoryTasks.filter(t => t.status === 'done').length,
      'in-progress': categoryTasks.filter(t => t.status === 'in-progress').length,
      planned: categoryTasks.filter(t => t.status === 'planned').length,
      pending: categoryTasks.filter(t => t.status === 'pending').length,
      todo: categoryTasks.filter(t => t.status === 'todo').length
    };
  }, [tasks, taskFilter]);
  // Render customers only view
  if (viewMode === 'customers') {
    return <div data-id="element-1357" data-mp="33wwxz6wuxg5d">
        <header className="mb-8" data-id="element-1358" data-mp="33x5auyszclgk">
          <h1 className="text-4xl font-bold text-stone-900 mb-2" data-id="element-1359" data-mp="33w3xgnohrmvo">Kunde data</h1>
          <p className="text-stone-600" data-id="element-1360" data-mp="3im1e8ik3daau">
            Hold styr på dine kunder og deres information
          </p>
        </header>

        <section data-id="element-1361" data-mp="3rnitxztl79z0">
          <div className="flex items-center justify-between mb-6" data-id="element-1362" data-mp="3rmznbf9e080a">
            <div className="flex items-center gap-3" data-id="element-1363" data-mp="3rl6bzmntwzly">
              <div className="p-2 bg-teal-100 rounded-lg" data-id="element-1364" data-mp="3mpr215sxznut">
                <UsersIcon className="w-5 h-5 text-teal-700" data-id="element-1365" data-mp="1kzigc8h3ulf6" />
              </div>
              <div data-id="element-1366" data-mp="1kxfrhrrouyc1">
                <h2 className="text-2xl font-semibold text-stone-900" data-id="element-1367" data-mp="1py3iwejtxh6s">
                  Kunder
                </h2>
                <p className="text-sm text-stone-500" data-id="element-1368" data-mp="1uoto9rs99k1h">
                  {filteredCustomers.length} af {customers.length}
                </p>
              </div>
            </div>
            {!isReadOnly && <Button onClick={onOpenCustomerModal} size="sm" className="flex items-center gap-2" data-id="element-1369" data-mp="121fws97btgor">
                <PlusIcon className="w-4 h-4" data-id="element-1370" data-mp="1b12ryd9gpq9d" />
                Tilføj kunde
              </Button>}
          </div>

          {/* Customer Search */}
          {customers.length > 0 && <div className="mb-4 relative" data-id="element-1371" data-mp="2mghwc2403kei">
              <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" data-id="element-1372" data-mp="2mfg8sy5st5fw" />
              <input type="text" placeholder="Søg efter kunde..." value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} className="w-full pl-11 pr-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-1373" data-mp="2rdrhh5kcx9wj" />
            </div>}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1374" data-mp="2it1k2rftarlx">
            {customers.length === 0 ? <div className="col-span-full bg-white rounded-xl p-12 text-center border border-stone-100" data-id="element-1375" data-mp="2dsx555ujy4xt">
                <UsersIcon className="w-12 h-12 text-stone-300 mx-auto mb-4" data-id="element-1376" data-mp="28v3zcdx5vcy2" />
                <h3 className="text-lg font-medium text-stone-900 mb-2" data-id="element-1377" data-mp="24u1yjsnlistz">
                  Ingen kunder endnu
                </h3>
                <p className="text-stone-500 mb-6" data-id="element-1378" data-mp="72fslulb67dj">
                  {isReadOnly ? 'Der er ingen kunder i systemet' : 'Tilføj din første kunde for at komme i gang'}
                </p>
                {!isReadOnly && <Button onClick={onOpenCustomerModal} size="sm" data-id="element-1379" data-mp="21bcierwjldls">
                    Tilføj kunde
                  </Button>}
              </div> : filteredCustomers.length === 0 ? <div className="col-span-full bg-white rounded-xl p-12 text-center border border-stone-100" data-id="element-1380" data-mp="1rm2t1jt3291d">
                <SearchIcon className="w-12 h-12 text-stone-300 mx-auto mb-4" data-id="element-1381" data-mp="2fbdl8cwdav3w" />
                <h3 className="text-lg font-medium text-stone-900 mb-2" data-id="element-1382" data-mp="2k8vwi9xuxmf2">
                  Ingen kunder matcher din søgning
                </h3>
                <p className="text-stone-500" data-id="element-1383" data-mp="26ca0z5lgs9nt">Prøv at justere din søgning</p>
              </div> : filteredCustomers.map((customer, index) => <CustomerCard key={customer.id} customer={customer} index={index} onEdit={onEditCustomer} onDelete={onDeleteCustomer} onViewDetail={onViewCustomerDetail} readOnly={isReadOnly} data-id="element-1384" data-mp="utfpb03huart" data-mpi={index} />)}
          </div>
        </section>
      </div>;
  }
  // Render tasks only view
  return <div data-id="element-1385" data-mp="wcfr7yy2y9h7">
      <header className="mb-8" data-id="element-1386" data-mp="wbwhh5il2re9">
        <h1 className="text-4xl font-bold text-stone-900 mb-2" data-id="element-1387" data-mp="wcpe3dntw0io">Opgaver</h1>
        <p className="text-stone-600" data-id="element-1388" data-mp="8nn0qi088ttz">
          Hold styr på alle dine opgaver og deres status
        </p>
      </header>

      <section data-id="element-1389" data-mp="3p3eshlmvi1m">
        <div className="flex items-center justify-between mb-6" data-id="element-1390" data-mp="3lfixbspujjq">
          <div className="flex items-center gap-3" data-id="element-1391" data-mp="idg8f7x90osy">
            <div className="p-2 bg-amber-100 rounded-lg" data-id="element-1392" data-mp="dloimzlx2n3v">
              <ListTodoIcon className="w-5 h-5 text-amber-700" data-id="element-1393" data-mp="dk3p9dbc9a7l" />
            </div>
            <div data-id="element-1394" data-mp="3m0gdyh5ipvph">
              <h2 className="text-2xl font-semibold text-stone-900" data-id="element-1395" data-mp="3lzx5rs5o6lon">Opgaver</h2>
              <p className="text-sm text-stone-500" data-id="element-1396" data-mp="3h1dk7suznbwt">
                {filteredTasks.length} af {statusCounts.all}
              </p>
            </div>
          </div>
          {!isReadOnly && <Button onClick={onOpenTaskModal} size="sm" className="flex items-center gap-2" disabled={customers.length === 0} data-id="element-1397" data-mp="30j83ecshvfph">
              <PlusIcon className="w-4 h-4" data-id="element-1398" data-mp="3ahe3couqqbrp" />
              Tilføj opgave
            </Button>}
        </div>

        {/* Category Filter Buttons */}
        <div className="flex gap-2 mb-4" data-id="element-1399" data-mp="3jh16bf34bpmv">
          <button onClick={() => setTaskFilter('Ladeboks')} className={`flex-1 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${taskFilter === 'Ladeboks' ? 'bg-purple-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1400" data-mp="3jfzjkdcqpepb">
            <div className="flex items-center justify-center gap-2" data-id="element-1401" data-mp="21kmg8whyya4">
              <ZapIcon className="w-4 h-4" data-id="element-1402" data-mp="61v44itnjd4t" />
              <span data-id="element-1403" data-mp="62xumpn2bdk7">Ladeboks opgaver</span>
            </div>
          </button>
          <button onClick={() => setTaskFilter('Andet')} className={`flex-1 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${taskFilter === 'Andet' ? 'bg-teal-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1404" data-mp="1qkfbbtgmt09k">
            <div className="flex items-center justify-center gap-2" data-id="element-1405" data-mp="1ckxi91waz0ue">
              <PackageIcon className="w-4 h-4" data-id="element-1406" data-mp="1cmqtkuhv298q" />
              <span data-id="element-1407" data-mp="2a74oshc6339e">Alm. Installation</span>
            </div>
          </button>
        </div>

        {/* Status Filter Buttons */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4" data-id="element-1408" data-mp="259uzd1os2rlk">
          <button onClick={() => setStatusFilter('all')} className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${statusFilter === 'all' ? 'bg-stone-900 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1409" data-mp="20hbfjmzhz08b">
            <div className="flex items-center justify-center gap-2" data-id="element-1410" data-mp="2o7ntbehfbr6t">
              <ListTodoIcon className="w-4 h-4" data-id="element-1411" data-mp="1pyf81ootemeg" />
              <span data-id="element-1412" data-mp="1pz6ry291uvow">Alle</span>
              <span className="ml-1 text-xs opacity-75" data-id="element-1413" data-mp="1q0hxo7m22din">
                ({statusCounts.all})
              </span>
            </div>
          </button>
          <button onClick={() => setStatusFilter('done')} className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${statusFilter === 'done' ? 'bg-emerald-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1414" data-mp="1zwuzjsrr7xoh">
            <div className="flex items-center justify-center gap-2" data-id="element-1415" data-mp="23yq0ux9oqb29">
              <CheckCircleIcon className="w-4 h-4" data-id="element-1416" data-mp="23wc6kqmtx2vr" />
              <span data-id="element-1417" data-mp="2im1bmwy1lr1b">Afsluttet</span>
              <span className="ml-1 text-xs opacity-75" data-id="element-1418" data-mp="2ip5loay6vgzw">
                ({statusCounts.done})
              </span>
            </div>
          </button>
          <button onClick={() => setStatusFilter('in-progress')} className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${statusFilter === 'in-progress' ? 'bg-amber-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1419" data-mp="2rmzj5p7owgon">
            <div className="flex items-center justify-center gap-2" data-id="element-1420" data-mp="3b9swtafdnci3">
              <PlayCircleIcon className="w-4 h-4" data-id="element-1421" data-mp="2x9rv5srag11k" />
              <span data-id="element-1422" data-mp="2x900ck5im7cc">I gang</span>
              <span className="ml-1 text-xs opacity-75" data-id="element-1423" data-mp="2xc4bk1hee3ci">
                ({statusCounts['in-progress']})
              </span>
            </div>
          </button>
          <button onClick={() => setStatusFilter('todo')} className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${statusFilter === 'todo' ? 'bg-stone-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1424" data-mp="2nj6p5k9abu8m">
            <div className="flex items-center justify-center gap-2" data-id="element-1425" data-mp="2ik3xd1ibfkil">
              <ClockIcon className="w-4 h-4" data-id="element-1426" data-mp="24l493uypt7my" />
              <span data-id="element-1427" data-mp="24mf8lil4nt89">Afventende</span>
              <span className="ml-1 text-xs opacity-75" data-id="element-1428" data-mp="24ngmrvxfwvu7">
                ({statusCounts.todo})
              </span>
            </div>
          </button>
          <button onClick={() => setStatusFilter('planned')} className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${statusFilter === 'planned' ? 'bg-blue-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1429" data-mp="f50gn0gme7gr">
            <div className="flex items-center justify-center gap-2" data-id="element-1430" data-mp="1ddam8wvlkks">
              <CalendarIcon className="w-4 h-4" data-id="element-1431" data-mp="y2ulr3emnytv" />
              <span data-id="element-1432" data-mp="y1jnfj3ybja5">Planlagt</span>
              <span className="ml-1 text-xs opacity-75" data-id="element-1433" data-mp="y0ia17zgqkp9">
                ({statusCounts.planned})
              </span>
            </div>
          </button>
          <button onClick={() => setStatusFilter('pending')} className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${statusFilter === 'pending' ? 'bg-purple-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1434" data-mp="p13gfzo01u0j">
            <div className="flex items-center justify-center gap-2" data-id="element-1435" data-mp="34t12zhk5broi">
              <ClockIcon className="w-4 h-4" data-id="element-1436" data-mp="2upm3n86gbp9t" />
              <span data-id="element-1437" data-mp="2uqowhlnc41sd">Afventende</span>
              <span className="ml-1 text-xs opacity-75" data-id="element-1438" data-mp="2unj49xjesjsf">
                ({statusCounts.pending})
              </span>
            </div>
          </button>
        </div>

        {/* Task Search */}
        {tasks.length > 0 && <div className="mb-4 relative" data-id="element-1439" data-mp="39es2uw5ahfhp">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" data-id="element-1440" data-mp="39fkyp22pmkl2" />
            <input type="text" placeholder="Søg efter opgave..." value={taskSearch} onChange={e => setTaskSearch(e.target.value)} className="w-full pl-11 pr-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-1441" data-mp="3dflotjz68k7y" />
          </div>}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1442" data-mp="1xyl94e7qyjuq">
          {tasks.length === 0 ? <div className="col-span-full bg-white rounded-xl p-12 text-center border border-stone-100" data-id="element-1443" data-mp="3offdtfcwx7ic">
              <ListTodoIcon className="w-12 h-12 text-stone-300 mx-auto mb-4" data-id="element-1444" data-mp="3jhx0zdvry852" />
              <h3 className="text-lg font-medium text-stone-900 mb-2" data-id="element-1445" data-mp="1b91c3jhud6s">
                Ingen opgaver endnu
              </h3>
              <p className="text-stone-500 mb-6" data-id="element-1446" data-mp="3sivagbozf7v1">
                {isReadOnly ? 'Der er ingen opgaver i systemet' : customers.length === 0 ? 'Tilføj en kunde først, derefter kan du oprette opgaver' : 'Tilføj din første opgave for at komme i gang'}
              </p>
              {!isReadOnly && customers.length > 0 && <Button onClick={onOpenTaskModal} size="sm" data-id="element-1447" data-mp="39q9elre2rhag">
                  Tilføj opgave
                </Button>}
            </div> : filteredTasks.length === 0 ? <div className="col-span-full bg-white rounded-xl p-12 text-center border border-stone-100" data-id="element-1448" data-mp="2h1lqzp5usftq">
              <SearchIcon className="w-12 h-12 text-stone-300 mx-auto mb-4" data-id="element-1449" data-mp="wjjiriwx7l0l" />
              <h3 className="text-lg font-medium text-stone-900 mb-2" data-id="element-1450" data-mp="11buhwjju72s3">
                Ingen opgaver matcher dine filtre
              </h3>
              <p className="text-stone-500" data-id="element-1451" data-mp="nlhaqjerrs3t">
                Prøv at justere kategori, status eller søgning
              </p>
            </div> : filteredTasks.map((task, index) => <TaskCard key={task.id} task={task} index={index} onClick={() => onEditTask(task)} onDelete={onDeleteTask} readOnly={isReadOnly} data-id="element-1452" data-mp="1g6h93yokwtbm" data-mpi={index} />)}
        </div>
      </section>
    </div>;
}